import type {
  MbcBriefQuality,
  MbcBriefQualityItem,
  MbcBriefQualityVariation,
} from "@nlc/shared-types";

import { Check, Minus, X } from "lucide-react";

import { ProgressBar } from "@/components/progress-bar";
import { cn } from "@/utils/cn";
import { scoreColor } from "@/utils/scoreColor";

const BAND_LABELS: Record<MbcBriefQuality["band"], string> = {
  good: "Strong",
  medium: "Moderate",
  poor: "Needs work",
};

const VERDICT_LABELS: Record<MbcBriefQualityVariation["quality"], string> = {
  bad: "Weak",
  good: "Good",
  neutral: "Could be better",
};

const VERDICT_DOT: Record<MbcBriefQualityVariation["quality"], string> = {
  bad: "bg-utility-error-500",
  good: "bg-utility-success-500",
  neutral: "bg-utility-warning-500",
};

type ItemStatus = "needs-attention" | "not-judged" | "optional" | "passes";

function itemStatus(item: MbcBriefQualityItem): ItemStatus {
  if (item.passes) return "passes";
  if (!item.required && !item.present) return "optional";
  // Present but no verdict matches the current text (e.g. judge outage).
  if (item.kind === "judged" && item.present && item.score === null) {
    return "not-judged";
  }

  return "needs-attention";
}

function StatusIcon({ status }: { status: ItemStatus }) {
  if (status === "passes") {
    return (
      <span
        aria-label="Passes"
        className="mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-full bg-utility-success-50 text-utility-success-700"
        role="img"
      >
        <Check className="size-3" strokeWidth={3} />
      </span>
    );
  }

  if (status === "needs-attention") {
    return (
      <span
        aria-label="Needs attention"
        className="mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-full bg-utility-error-50 text-utility-error-700"
        role="img"
      >
        <X className="size-3" strokeWidth={3} />
      </span>
    );
  }

  return (
    <span
      aria-label={status === "optional" ? "Optional" : "Not judged yet"}
      className="mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-full bg-utility-gray-50 text-utility-gray-700"
      role="img"
    >
      <Minus className="size-3" strokeWidth={3} />
    </span>
  );
}

function VariationVerdicts({
  variations,
}: {
  variations: MbcBriefQualityVariation[];
}) {
  // A single design has no numbered blocks, so there is nothing to name.
  const showHeading = variations.length > 1;
  // Without a heading, a verdict with no suggestion is a bare dot that only
  // repeats the item's own status icon.
  const shown = showHeading
    ? variations
    : variations.filter((verdict) => verdict.suggestion);

  if (shown.length === 0) return null;

  return (
    <ul className="mt-1 flex flex-col gap-1">
      {shown.map((verdict) => (
        <li className="flex items-start gap-1.5" key={verdict.variation}>
          <span
            aria-label={VERDICT_LABELS[verdict.quality]}
            className={cn(
              "mt-1.5 size-1.5 shrink-0 rounded-full",
              VERDICT_DOT[verdict.quality],
            )}
            role="img"
          />
          <div className="text-xs">
            {showHeading ? (
              <p className="font-medium text-text-secondary">
                {verdict.label
                  ? `Variation ${verdict.variation} - ${verdict.label}`
                  : `Variation ${verdict.variation}`}
              </p>
            ) : null}
            {verdict.suggestion ? (
              <p className="text-text-tertiary">{verdict.suggestion}</p>
            ) : null}
          </div>
        </li>
      ))}
    </ul>
  );
}

type BriefQualityCardProps = {
  quality: MbcBriefQuality | null;
};

/** Shared between the chat page's sidebar and the review page. Each criterion
 * arrives already joined with its per-variation verdicts — this only
 * renders. Nothing until the brief endpoint has answered once for the thread. */
export function BriefQualityCard({ quality }: BriefQualityCardProps) {
  if (!quality) return null;

  return (
    <section className="rounded-2xl border border-border-primary bg-bg-primary p-5 shadow-sm">
      <h3 className="text-lg font-semibold text-text-primary">
        Brief quality score
      </h3>

      <div className="mt-4 flex items-end justify-between">
        <span
          className="text-sm font-semibold"
          style={{ color: scoreColor(quality.score) }}
        >
          {BAND_LABELS[quality.band]}
        </span>
        <span
          className="text-2xl font-bold"
          style={{ color: scoreColor(quality.score) }}
        >
          {quality.score}%
        </span>
      </div>
      <div className="mt-2">
        <ProgressBar
          fillColor={scoreColor(quality.score)}
          showValue={false}
          value={quality.score}
        />
      </div>

      <ul className="mt-5 flex flex-col gap-3">
        {quality.items.map((item) => (
          <li className="flex items-start gap-2" key={item.id}>
            <StatusIcon status={itemStatus(item)} />
            <div className="min-w-0 flex-1">
              <div className="flex items-baseline justify-between gap-2">
                <p className="text-sm text-text-secondary">{item.label}</p>
                {item.kind === "judged" && item.score !== null ? (
                  <span className="shrink-0 text-xs font-semibold text-text-tertiary">
                    {item.score}%
                  </span>
                ) : null}
              </div>
              {item.variations.length > 0 ? (
                <VariationVerdicts variations={item.variations} />
              ) : null}
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}
